<template>
  <ion-page>
    <ion-loading
        :is-open="isLoading"
        :message="$t(loadingMessage)"
        spinner="circles"
    />
    <ion-content class="auth-background" :scroll-y="false">
      <div class="login-wrapper">
        <ion-card v-if="isNative && !hasChainServerUrl" class="login-card qr-setup-card">
          <div class="qr-setup-content">
            <ion-icon :icon="allIcons.qrCodeOutline" class="setup-icon"></ion-icon>
            <i class="fa-solid fa-qrcode setup-icon"></i>
            <h2>{{ $t('auth.deviceAuth') }}</h2>
            <p>{{ $t('auth.scanDescription') }}</p>

            <ion-button expand="block" @click="triggerQrScan" class="scan-button" :disabled="isScanning">
              <ion-icon slot="start" :icon="allIcons.cameraOutline"></ion-icon>
              <i slot="start" class="fa-solid fa-camera btn-meta-icon"></i>
              <span v-if="isScanning">Processing...</span>
              <span v-else>{{ $t('auth.scanNow') }}</span>
            </ion-button>
            <p v-if="scannerErrorMessage" class="error-text">
              <ion-icon :icon="allIcons.informationCircleOutline"></ion-icon>
              <i class="fa-solid fa-circle-info"></i>
              {{ $t(scannerErrorMessage) }}
            </p>
          </div>
        </ion-card>

        <div v-else class="card-container">
          <ion-card class="login-card">
            <div class="card-header-gradient" />

            <div class="card-header">
              <h2 class="title">{{ $t('auth.welcome') }}</h2>
              <p class="subtitle">{{ $t('auth.subtitleWelcome') }}</p>
            </div>

            <div class="card-body">
              <div class="sso-buttons" v-if="settingsStore.suiteSettings?.providers?.length">
                <suite-button
                  v-for="provider in settingsStore.suiteSettings.providers"
                  :key="provider.providerId"
                  :icon="(allIcons as any)[provider.icon] || allIcons.helpOutline"
                  fill="outline"
                  @click="handleSSO(provider.providerId)"
                  full-width
                >
                  {{ t('auth.loginWith') }} <span class="bold-600">{{ provider.label }}</span>
                </suite-button>
              </div>
              <p v-if="displayError" class="error-text">
                <ion-icon :icon="allIcons.informationCircleOutline"></ion-icon>
                <i class="fa-solid fa-circle-info"></i>
                {{ displayError }}
              </p>
            </div>
          </ion-card>

          <div class="powered-by">
            <span>{{ t('home.poweredBy') }}</span>
            <ion-icon class="logo" :src="heilaLogo"></ion-icon>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script setup lang="ts">
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import heilaLogo from '../../assets/heila_logo.svg';
import {useI18n} from 'vue-i18n';
import {IonButton, IonCard, IonCol, IonContent, IonGrid, IonIcon, IonLoading, IonPage, IonRow} from '@ionic/vue';
import * as allIcons from 'ionicons/icons';
import {App, type URLOpenListenerEvent} from '@capacitor/app';
import {Capacitor, type PluginListenerHandle} from '@capacitor/core';
import {GoogleLoginResponseOnline, SocialLogin} from '@capgo/capacitor-social-login';
import {useNavigation} from '@/composables/useNavigation';
import {useAuth} from '@/composables/useAuth';
import {useSettingsStore} from '@/stores/useSettingsStore';

import {PermissionService} from "@/services/PermissionService.ts";
import SendIframe from "@/services/IframeBridge/iframes/delivery/outbound.ts";

import {isNative} from "@/utils/platform.ts";
import {AppConfigResponse} from '@/models/auth';
import {useServiceStore} from "@/stores/useServiceStore.ts";
import SuiteButton from "@/components/SuiteButton.vue";
import {StorageService} from "@/services/storageService.ts";
import {UserPreferences} from "@/models/settings.ts";

const {t, te} = useI18n();
const {goTo, getQueryParam} = useNavigation();

const isScanning = ref(false);
const scannerErrorMessage = ref('');

const {
  isLoading,
  errorMessage,
  hasChainServerUrl,
  initAuth,
  handleLogin,
  setAuthAppConf
} = useAuth();
const serviceStore = useServiceStore();
const settingsStore = useSettingsStore();

const redirectOnLogin = ref('/');
const redirParam = ref('/');
const loadingMessage = ref('auth.loading_default');
const listeners: Array<PluginListenerHandle> = [];

const getPersistedProvider = async (): Promise<string | null> => {
  return await StorageService.Get<string>("LAST_AUTH_PROVIDER" as any);
};

const handleAuthResponse = async (response: any) => {
  if (!response) return;

  if (response.authUrl && response.authUrl !== '') {
    return goTo(response.authUrl);
  }

    const basePath = response.redir !== '' ? response.redir : redirectOnLogin.value;
    const navigationState: Record<string, any> = {};
    if (response.token && response.token !== '') {
      navigationState.token = response.token;
      navigationState.tokenSource = response.tokenSource;
    }
    if (response.status && response.status !== '') {
      navigationState.status = response.status;
    }

  goTo(basePath, {
    state: navigationState,
    replace: true
  });
};

const performLogin = async (code: string, state: string, redir: string = redirParam.value, provider: string) => {
  loadingMessage.value = t('auth.verifying');
  try {
    const response = await handleLogin(code, state, redir, provider);
    await initAuth();

    const {processPendingNotification} = await import('@/services/NotificationHandler');
    const handled = await processPendingNotification(true);
    if (!handled) {
      handleAuthResponse(response);
    }
  } catch (err) {
    console.error("Login execution failed:", err);
  }
};

const parseUrlAndLogin = async (urlString: string) => {
  try {
    const url = new URL(urlString)
    const code = url.searchParams.get('code');
    const state = url.searchParams.get('state');

    if (code && state) {
      const provider = await getPersistedProvider();
      if (!provider) {
        errorMessage.value = 'auth.errors.missingProvider';
        return;
      }
      await performLogin(code as string, state as string, redirParam.value, provider);
    }
  } catch (e) {
    console.error("Error parsing deep link URL:", e);
  }
};

const triggerQrScan = async () => {
  // bypass scan
  // await setAuthAppConf({chainServerUrl: "http://192.168.2.17"});
  // if (!settingsStore.hasSuiteSettings) {
  //   await settingsStore.fetchSuiteSettings();
  // }
  // await initSocialLogin();

  isScanning.value = true;
  const resp = await PermissionService.scanOnBoardingAndProcess<AppConfigResponse>();
  if (resp.success) {
    await setAuthAppConf(resp.data);
    if (!settingsStore.hasSuiteSettings) {
      await settingsStore.fetchSuiteSettings();
    }
    await initSocialLogin();
  } else {
    scannerErrorMessage.value = resp.error.msg
  }
  isScanning.value = false;
};

onBeforeUnmount(() => {
  while (listeners.length > 0) {
    const listener = listeners.pop();
    listener?.remove();
  }
});

const displayError = computed(() => {
  const genericError = t('auth.errors.generic_error');
  if (errorMessage.value) {
    return te(errorMessage.value) ? t(errorMessage.value) : genericError;
  }
  const stateError = getQueryParam('err');
  if (stateError) {
    const fullPath = `auth.errors.${stateError}`;
    return te(fullPath) ? t(fullPath) : genericError;
  }
});

onMounted(async () => {
  await settingsStore.fetchSuiteSettings();
  await initSocialLogin();

  const preferences = await StorageService.Get<UserPreferences>("USER_PREFERENCES");
  if (preferences) {
    if (Capacitor.isNativePlatform()) { // todo need to also check if web mobile
      if (preferences.defaultAppMobile) {
        redirectOnLogin.value = preferences.defaultAppMobile;
      }
    } else {
      if (preferences.defaultAppDesktop) {
        redirectOnLogin.value = preferences.defaultAppDesktop;
      }
    }
  }

  redirParam.value = getQueryParam('redir') || '/';
  const code = getQueryParam('code');
  const state = getQueryParam('state');
  if (code && state) {
    const provider = await getPersistedProvider();
    if (!provider) {
      errorMessage.value = 'auth.errors.missingProvider';
      return;
    }
    await performLogin(code as string, state as string, redirParam.value, provider);
  }

  listeners.push(await App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    console.log('App opened with URL:', event.url);
    parseUrlAndLogin(event.url);
  }))

  const launchUrl = await App.getLaunchUrl();
  if (launchUrl?.url) {
    parseUrlAndLogin(launchUrl.url);
  }
});

const initSocialLogin = async () => {
  if (!Capacitor.isNativePlatform()) return;

  const cfg = settingsStore.suiteSettings?.socialLoginConfig;

  if (!cfg) {
    console.warn("SocialLogin not initialized: missing config");
    return;
  }

  try {
    console.log("Initializing SocialLogin...");

    await SocialLogin.initialize({
      google: {
        webClientId: cfg.google.webClientId,
        iOSClientId: cfg.google.iosClientId
      },
      oauth2: {
        azure: {
          clientId: cfg.azure.clientId,
          redirectUrl: 'com.heilasystems.suite://login',
          scope: 'openid profile email User.Read',
          pkceEnabled: true,
          resourceUrl: 'https://graph.microsoft.com/v1.0/me',
          authorizationBaseUrl: `https://login.microsoftonline.com/${cfg.azure.tenantId}/oauth2/v2.0/authorize`,
          accessTokenEndpoint: `https://login.microsoftonline.com/${cfg.azure.tenantId}/oauth2/v2.0/token`,
        }
      }
    });

    console.log("SocialLogin initialized.");
  } catch (e) {
    console.error("SocialLogin init failed", e);
  }
};

const handleSSO = async (provider: string) => {
  if (window.history.state?.err) {
    window.history.replaceState({...window.history.state, err: null}, '');
  }

  await StorageService.Set("LAST_AUTH_PROVIDER", provider);

  if (provider === 'google' && Capacitor.isNativePlatform()) {
    try {
      isLoading.value = true;
      const googleUser = await SocialLogin.login({
        provider: 'google',
        options: {},
      });

      const result = googleUser.result as GoogleLoginResponseOnline;
      const idToken = result.idToken;
      const response = await handleLogin(idToken, provider, redirParam.value, provider);
      await initAuth();

      const {processPendingNotification} = await import('@/services/NotificationHandler');
      const handled = await processPendingNotification(true);
      if (!handled) {
        handleAuthResponse(response);
      }

    } catch (err) {
      console.error("Native "+provider+" Login failed:", err);
    } finally {
      isLoading.value = false;
    }
  }  else if (provider === 'microsoft' && Capacitor.isNativePlatform()) {
      try {
        isLoading.value = true;
        const microsoftUser = await SocialLogin.login({
          provider: 'oauth2',
          options: {
            providerId: 'azure',
          }
        });

        const result = microsoftUser.result;
        const idToken = result.idToken;
        const response = await handleLogin(idToken, provider, redirParam.value, provider);
        await initAuth();

        const {processPendingNotification} = await import('@/services/NotificationHandler');
        const handled = await processPendingNotification(true);
        if (!handled) {
          handleAuthResponse(response);
        }

      } catch (err) {
        console.error("Native "+provider+" Login failed:", err);
      } finally {
        isLoading.value = false;
      }
  } else {
    isLoading.value = true;
    const response = await handleLogin(null, null, redirParam.value, provider);
    await initAuth();
      handleAuthResponse(response);
  }
};
</script>

<style scoped>
.auth-background {
  --background: #ffffff;
}

/* Centralized Card Core Wrappers */
.login-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: #ffffff;
}

.card-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 365px;
  margin: 16px;
}

.login-card {
  width: 100%;
  background: #ffffff;
  border-radius: 38px;
  margin: 0;
  box-shadow: -4px 4px 16px rgba(0, 0, 0, 0.2);
  overflow: hidden; /* Ensures gradient cuts neatly with the border radius */
}

.card-header {
  padding: 32px;
  padding-bottom: 20px;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.card-header .title, .card-header .subtitle {
  width: fit-content;
}

.card-header-gradient {
  position: absolute;
  width: 100%;
  height: 100%;
  background: var(--grediant-color);
  z-index: -1;
}

.card-header h2 {
  font-size: 2.2rem;
  margin: 0;
  color: #ffffff;
}

.card-header .subtitle {
  color: #ffffff;
  font-size: 1.05rem;
  margin: 0;
  opacity: 0.95;
  font-weight: 400;
}

.card-body {
  background: #ffffff;
  border-radius: 28px;
  padding: 32px 28px;
  min-height: 140px;
}

/* SSO Single Row Custom Framework */
.sso-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
}


/* UI Meta Details Elements */
.powered-by {
  color: #707070;
  font-size: 0.85rem;
  margin-top: 20px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 4px;
}

.error-text {
  color: #eb445a;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
  margin: 16px 4px 0 4px;
}

/* Native QR Section Syncing styling */
.qr-setup-card {
  padding: 40px 24px;
  text-align: center;
  max-width: 440px;
}

.setup-icon {
  font-size: 76px;
  color: #3DACC0;
  margin-bottom: 20px;
}

.qr-setup-content h2 {
  font-family: "Patua One", sans-serif;
  font-weight: 700;
  color: #363131;
  margin-bottom: 10px;
}

.qr-setup-content p {
  color: #64748b;
  margin-bottom: 30px;
  line-height: 1.5;
}

.scan-button {
  --border-radius: 12px;
  --background: linear-gradient(100deg, #8BBE2C 0%, #3DACC0 100%);
  --color: #ffffff;
  font-weight: 600;
  height: 50px;
}

.btn-meta-icon {
  margin-right: 8px;
  font-size: 16px;
}

.logo {
  width: 48px;
}

@media (max-width: 480px) {
  .card-header-gradient {
    padding: 36px 24px 32px 24px;
  }
  .card-header-gradient h2 {
    font-size: 1.9rem;
  }
  .card-body {
    padding: 28px 20px;
  }
}
</style>